import { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCreateTimeEntry, useDeleteTimeEntry, useTimeEntries, type TimeEntry } from "@/hooks/use-time-registration";
import { toast } from "sonner";
import { requireCommentForTrackedProject, requireCommentForReisetid } from "@/lib/time-entry-validation";
import { useEnsureReisetidLine } from "@/hooks/use-reisetid";
import type { MyTask } from "@/hooks/use-my-tasks";
import { formatHours, timeDiffMinutes, addMinutesToTime } from "@/lib/format";
import { useIsMobile } from "@/hooks/use-mobile";
import SmartTimeInput from "./SmartTimeInput";
import { useCompanyHolidays } from "@/hooks/use-company-holidays";
import { classifyDay, DAY_KIND_NB } from "@/lib/overtime-calc";
import TimelineBar from "./TimelineBar";
import { SrIcon } from "@/components/ui/SrIcon";
import TimeTrackingEntryLog from "./TimeTrackingEntryLog";

const PHASE_LABELS: Record<string, string> = {
  preprod: "Preproduksjon",
  production: "Produksjon",
  postprod: "Postproduksjon",
  reise: "Reise",
  // VIB-1208: synteisk fase-verdi for interne oppgaver (internalTaskToMyTask)
  // — kun brukt her for task_description-strengen, ikke en ekte DB-enum-verdi.
  intern: "Intern",
};

/** Check if a date string has a real time component (not midnight) */
function hasTimeComponent(dateStr: string): boolean {
  const d = new Date(dateStr);
  return d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
}

/** Extract HH:MM from an ISO date string */
function extractTime(dateStr: string): string {
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function TimeTrackingPopup({
  task,
  onClose,
  userId,
  assignmentId = null,
  initialDate,
}: {
  task: MyTask | any;
  onClose: () => void;
  userId: string | null;
  /**
   * VIB-1144: budget_line_assignment-id manuell-føringen skal kobles til, slik
   * at «brukt»-aggregeringen i use-my-tasks (assignment-first, VIB-671) teller
   * den. Settes av callere med ekte oppgaver; null for interne/reisetid (ingen
   * assignment), identisk med timer-stoppens logikk i TimerContext.
   */
  assignmentId?: string | null;
  /** @deprecated Beholdt for caller-kompatibilitet; popup-en sentreres nå. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  initialDate?: string;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [manualDate, setManualDate] = useState(initialDate ?? today);
  const [manualFrom, setManualFrom] = useState("");
  const [manualTo, setManualTo] = useState("");
  // VIB: logging across midnight (e.g. 22:00 -> 01:30). When on, the "Til" time
  // belongs to the next day and the duration spans the day boundary.
  const [nesteDag, setNesteDag] = useState(false);
  const [comment, setComment] = useState("");
  // VIB-1224: auto-forslag av helg-/
  // helligdagstimer. null = følg forslaget (= varigheten); streng = overstyrt.
  const [weekendOverride, setWeekendOverride] = useState<string | null>(null);
  // VIB-1287: overnatting på ALLE føringer (Erik 15.07 — ingen reise-gating).
  const [overnight, setOvernight] = useState(false);
  const [overnightNights, setOvernightNights] = useState("1");

  const isReisetid = task?.is_reisetid === true;
  const requiresComment = isReisetid || task?.is_internal === true || task?.project_type === 'hourly';
  const commentError = isReisetid
    ? requireCommentForReisetid(comment)
    : requiresComment
      ? requireCommentForTrackedProject(
          { is_internal: task?.is_internal, project_type: task?.project_type },
          comment
        )
      : null;
  const popupRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  // VIB-1220-regresjon (A3): RegistrationEditDialog (åpnet fra
  // TimeTrackingEntryLog under) portales til document.body, utenfor
  // popupRef. Uten denne flagget tolker click-outside-handlerne under ethvert
  // klikk inni den nestede dialogen som "utenfor" og lukker denne popupen.
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { mutate: createEntry, isPending: isSaving } = useCreateTimeEntry();
  const { mutateAsync: ensureReisetidLine } = useEnsureReisetidLine();
  const { mutate: deleteEntry } = useDeleteTimeEntry();
  const { data: entries = [] } = useTimeEntries(userId, "2000-01-01", "2099-12-31");

  // Filter entries for this task
  const taskEntries = useMemo(() => {
    if (!task) return [];
    const taskDesc = task.phase
      ? `${task.project_name} - ${PHASE_LABELS[task.phase] ?? task.phase} - ${task.task_description ?? ""}`
      : task.project_name;
    return entries.filter((e) =>
      e.project_id === task.project_id &&
      (e.task_description === taskDesc || e.task_description.includes(task.task_description ?? ""))
    );
  }, [entries, task]);

  // All entries for the selected date (across all tasks, for timeline + gap-filling)
  const dayEntries = useMemo(() => {
    return entries.filter((e) => {
      const entryDate = e.start_date.slice(0, 10);
      return entryDate === manualDate && hasTimeComponent(e.start_date);
    });
  }, [entries, manualDate]);

  // Timeline blocks from day entries
  const timelineBlocks = useMemo(() => {
    return dayEntries.map((e) => {
      const from = extractTime(e.start_date);
      const to = addMinutesToTime(from, Math.round(e.duration_seconds / 60));
      return { from, to };
    }).sort((a, b) => a.from.localeCompare(b.from));
  }, [dayEntries]);

  // Gap-filling: auto-set "from" to end of last entry on selected date
  useEffect(() => {
    if (manualFrom) return; // Don't overwrite if user already typed something
    if (timelineBlocks.length > 0) {
      const lastEnd = timelineBlocks[timelineBlocks.length - 1].to;
      setManualFrom(lastEnd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only auto-fill when timeline/date changes, not when user-typed manualFrom updates
  }, [timelineBlocks, manualDate]);

  // Duration calculation
  const durationMinutes = useMemo(() => {
    return timeDiffMinutes(manualFrom, manualTo, nesteDag);
  }, [manualFrom, manualTo, nesteDag]);

  // VIB-1224: helg-/helligdagsforslag
  const { data: holidays } = useCompanyHolidays(Number(manualDate.slice(0, 4)));
  const dayKind = useMemo(
    () => classifyDay(manualDate, holidays ?? new Set<string>()),
    [manualDate, holidays],
  );
  const isSpecialDay = dayKind !== "weekday";
  const suggestedWeekendHours =
    isSpecialDay && durationMinutes > 0
      ? Math.round((durationMinutes / 60) * 100) / 100
      : 0;
  const weekendHours =
    weekendOverride != null
      ? Number(weekendOverride.replace(",", ".")) || 0
      : suggestedWeekendHours;

  // Handle duration input in "To" field (e.g. "2t")
  const handleToDuration = (parsed: { hours: number; minutes: number }) => {
    if (manualFrom) {
      const totalMins = parsed.hours * 60 + parsed.minutes;
      setManualTo(addMinutesToTime(manualFrom, totalMins));
    }
  };

  // Quick add buttons
  const handleQuickAdd = (mins: number) => {
    const base = manualTo || manualFrom;
    if (base) setManualTo(addMinutesToTime(base, mins));
  };

  const handleNow = () => {
    const now = new Date();
    setManualTo(`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`);
  };

  const handleSave = async () => {
    if (!manualDate || !manualFrom || !manualTo || durationMinutes <= 0) {
      toast.error("Fyll inn gyldige tider");
      return;
    }
    if (commentError) {
      toast.error(commentError);
      return;
    }

    // VIB-909: reisetid logges fritt uten tildeling — budsjettlinjen (og det
    // dynamiske mersalget) opprettes/hentes on-demand via RPC før insert.
    let budgetLineId: string | null = task.budget_line_id || null;
    if (isReisetid && !budgetLineId) {
      try {
        budgetLineId = await ensureReisetidLine(task.project_id);
      } catch {
        toast.error("Kunne ikke opprette reisetid-linje");
        return;
      }
    }

    createEntry(
      {
        project_id: task.project_id,
        user_id: userId,
        task_description: task.phase
          ? `${task.project_name} - ${PHASE_LABELS[task.phase] ?? task.phase} - ${task.task_description}`
          : task.project_name,
        duration_seconds: durationMinutes * 60,
        start_date: new Date(`${manualDate}T${manualFrom}:00`).toISOString(),
        status: "registrert",
        // Internal projects have no budget line; toTask gives an empty
        // string which ?? would pass straight through to a uuid column
        // (22P02). Use || so "" collapses to null.
        budget_line_id: budgetLineId,
        // VIB-1144: koble manuell-føringen til assignment (mirror av timer-stopp
        // i TimerContext). Reisetid har ingen tildeling → null.
        assignment_id: isReisetid ? null : assignmentId,
        comment: comment.trim() || null,
        // VIB-1224 (bak flagg): auto-foreslått helg-/helligdagstillegg,
        // overstyrbart (også til 0) i feltet over Lagre-knappen.
        weekend_holiday_hours: isSpecialDay && weekendHours > 0 ? weekendHours : null,
        // VIB-1287: antall netter når «Overnatting» er krysset av.
        overnight_count: overnight ? Math.max(1, Number(overnightNights) || 1) : null,
      },
      {
        onSuccess: () => {
          // Lukk dialogen etter registrering (Erik 12.06). Tidligere ble den
          // hengende åpen for «chaining», men det opplevdes som at den ikke
          // forsvant. Success-toast eies av useCreateTimeEntry-hooken
          // («Timeregistrering opprettet») — ingen egen toast her, ellers ble
          // den doblet (feedback: dobbel toast på timeføringer).
          onClose();
        },
      }
    );
  };

  // Keyboard: Enter to save, Esc to close. In the comment textarea plain Enter
  // is a newline; ⌘/Ctrl+Enter saves from there, like project updates (VIB-2526).
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      const inTextarea = (e.target as HTMLElement).tagName === "TEXTAREA";
      if (inTextarea && !(e.metaKey || e.ctrlKey)) return;
      if (durationMinutes > 0) {
        e.preventDefault();
        handleSave();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const handleDeleteEntry = (entryId: string) => {
    deleteEntry(entryId, {
      onSuccess: () => toast.success("Timer slettet"),
      onError: (err) => toast.error((err as Error).message),
    });
  };

  // Close on click outside (desktop popup only; Sheet handles this natively).
  // Skipped entirely while the nested RegistrationEditDialog is open (VIB-1220-
  // regresjon, A3) — that dialog portals to document.body, outside popupRef,
  // so any interaction inside it would otherwise register as "outside" and
  // close this popup (and thereby unmount the edit dialog) mid-edit.
  useEffect(() => {
    if (isMobile || isEditDialogOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose, isMobile, isEditDialogOpen]);

  const body = (
    <>
      <TimelineBar entries={timelineBlocks} newFrom={manualFrom} newTo={manualTo} />

      <div className="px-4 py-3 space-y-4 xl:space-y-3 xl:py-3">
        {/* Date */}
        <div>
          <label className="text-sm xl:text-xs font-medium text-muted-foreground mb-1.5 xl:mb-1 block">Dato</label>
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              type="date"
              value={manualDate}
              onChange={(e) => {
                setManualDate(e.target.value);
                setManualFrom("");
                setManualTo("");
                setNesteDag(false);
                setWeekendOverride(null);
              }}
              className="h-12 xl:h-8 text-base xl:text-xs w-full xl:w-36"
            />
            <label className="flex items-center gap-1.5 text-sm xl:text-xs text-muted-foreground whitespace-nowrap cursor-pointer select-none">
              <input
                type="checkbox"
                checked={nesteDag}
                onChange={(e) => setNesteDag(e.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              Til neste dag
            </label>
          </div>
        </div>

        {/* From / To */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm xl:text-xs font-medium text-muted-foreground mb-1.5 xl:mb-1 block">Fra</label>
            <SmartTimeInput
              value={manualFrom}
              onChange={setManualFrom}
              placeholder='f.eks. "8", "0830"'
              autoFocus={!isMobile}
            />
          </div>
          <div>
            <label className="text-sm xl:text-xs font-medium text-muted-foreground mb-1.5 xl:mb-1 block">Til</label>
            <SmartTimeInput
              value={manualTo}
              onChange={setManualTo}
              onDuration={handleToDuration}
              placeholder='f.eks. "2t", "1030"'
            />
          </div>
        </div>

        {/* Quick buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground mr-0.5">Hurtig:</span>
          {[
            { label: "+15m", mins: 15 },
            { label: "+30m", mins: 30 },
            { label: "+1t", mins: 60 },
            { label: "+2t", mins: 120 },
          ].map((b) => (
            <button
              key={b.label}
              onClick={() => handleQuickAdd(b.mins)}
              className="h-9 xl:h-8 px-3 border-[1.5px] border-foreground bg-card text-sm xl:text-xs font-medium text-foreground hover:bg-muted transition-colors"
            >
              {b.label}
            </button>
          ))}
          <button
            onClick={handleNow}
            className="h-9 xl:h-8 px-3 border-[1.5px] border-primary bg-primary text-sm xl:text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Nå
          </button>
        </div>

        {/* Duration display */}
        {durationMinutes > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 border-[1.5px] border-foreground bg-card">
            <SrIcon name="clock" className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-sm font-medium tabular-nums">{formatHours(durationMinutes / 60)}</span>
            <span className="text-xs text-muted-foreground tabular-nums">({manualFrom} → {manualTo}{nesteDag ? " +1d" : ""})</span>
          </div>
        )}

        {/* VIB-1224: synlig helg-/helligdagsforslag */}
        {isSpecialDay && (
          <div className="flex items-center gap-2 px-3 py-2 border-[1.5px] border-amber-500/60 bg-amber-50 flex-wrap">
            <SrIcon name="calendar" className="w-4 h-4 text-amber-700 flex-shrink-0" />
            <span className="text-xs font-medium text-amber-900">
              {DAY_KIND_NB[dayKind]} — tillegg foreslått
            </span>
            <div className="flex items-center gap-1 ml-auto">
              <Input
                type="text"
                inputMode="decimal"
                aria-label="Helg-/helligdagstimer"
                value={weekendOverride ?? String(suggestedWeekendHours).replace(".", ",")}
                onChange={(e) => setWeekendOverride(e.target.value)}
                className="h-8 w-16 text-xs text-right"
              />
              <span className="text-xs text-amber-900">t</span>
            </div>
          </div>
        )}

        {/* VIB-1287: overnatting — synlig avkrysning på alle føringer */}
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-1.5 text-sm xl:text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={overnight}
              onChange={(e) => setOvernight(e.target.checked)}
              className="h-4 w-4 accent-primary"
              aria-label="Overnatting"
            />
            Overnatting
          </label>
          {overnight && (
            <div className="flex items-center gap-1.5">
              <Input
                type="number"
                min={1}
                value={overnightNights}
                onChange={(e) => setOvernightNights(e.target.value)}
                className="h-8 w-16 text-xs text-right"
                aria-label="Antall netter"
              />
              <span className="text-xs text-muted-foreground">netter</span>
            </div>
          )}
        </div>

        {/* Comment — required for internal and T&M projects */}
        <div>
          <label className="text-sm xl:text-xs font-medium text-muted-foreground mb-1.5 xl:mb-1 block">
            Kommentar{requiresComment && (
              <span className="text-destructive ml-1">(påkrevd)</span>
            )}
          </label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Hva brukte du tiden til?"
            className="text-sm xl:text-xs resize-none"
            rows={2}
          />
        </div>
      </div>

      {/* Existing entries (VIB-1220: rediger + slett) */}
      <TimeTrackingEntryLog
        entries={taskEntries}
        onDelete={handleDeleteEntry}
        onEditDialogOpenChange={setIsEditDialogOpen}
      />
    </>
  );

  const footer = (
    <div className="px-4 py-3 border-t-[1.5px] border-foreground bg-muted/30 flex items-center gap-2 flex-shrink-0">
      <p className="text-[10px] text-muted-foreground hidden xl:block flex-1">
        Skriv "8" for 08:00, "2t" for varighet, Enter for å lagre (⌘Enter fra kommentarfeltet)
      </p>
      <Button
        variant="ghost"
        className="h-10 xl:h-8 text-sm xl:text-xs"
        onClick={onClose}
      >
        Avbryt
      </Button>
      <Button
        className="h-10 xl:h-8 text-sm xl:text-xs gap-2"
        onClick={handleSave}
        disabled={durationMinutes <= 0 || isSaving || !!commentError}
      >
        Lagre <SrIcon name="arrow-right" className="w-3.5 h-3.5" />
      </Button>
    </div>
  );

  // Mobile: bottom sheet (iOS-native feel)
  if (isMobile) {
    return (
      <Sheet open onOpenChange={(o) => !o && onClose()}>
        <SheetContent
          side="bottom"
          className="p-0 flex flex-col max-h-[90vh] border-t-[1.5px] border-foreground"
          onKeyDown={handleKeyDown}
        >
          <SheetHeader className="px-4 py-3 border-b-[1.5px] border-foreground text-left space-y-0.5">
            <SheetTitle className="text-base font-display font-semibold">Logg tid</SheetTitle>
            <p className="text-xs text-muted-foreground truncate font-normal">
              {task.project_name} — {task.task_description}
            </p>
          </SheetHeader>
          <div className="overflow-y-auto flex-1">{body}</div>
          {footer}
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: centered modal
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/20 p-4">
      <div
        ref={popupRef}
        className="border-[1.5px] border-foreground bg-card shadow-[0_30px_80px_-40px_rgba(8,31,59,.35)] w-[460px] flex flex-col max-h-[calc(100vh-32px)]"
        onKeyDown={handleKeyDown}
      >
      <div className="border-b-[1.5px] border-foreground px-4 py-3 flex justify-between items-start gap-2 flex-shrink-0">
        <div className="min-w-0 flex-1">
          <p className="font-display font-semibold text-[18px] tracking-tight leading-tight">Logg tid</p>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{task.project_name} — {task.task_description}</p>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center hover:bg-muted flex-shrink-0 transition-colors"
          aria-label="Lukk"
        >
          <SrIcon name="x" className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
      <div className="overflow-y-auto flex-1">{body}</div>
      {footer}
      </div>
    </div>,
    document.body
  );
}
